import { useState, useEffect, useRef } from "react";
import { Building, Shield, ChevronDown, Search, Upload, X, ImageIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../../../utils/Images";
import { Link, useNavigate } from "react-router-dom";
import regions from "../../../data/regions";
import locations from "../../../data/locations";
import providerAuthService from "../../../api/ProviderAuthService";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { setNewUser } from "../../../store/slices/authSlice";

export default function SignUp() {
  const [currentStep, setCurrentStep] = useState(1);
  const progressRef = useRef(null);
  const nodeRefs = useRef([]);
  const [railMetrics, setRailMetrics] = useState({ left: 0, width: 0 });
  const [formData, setFormData] = useState({
    // Step 1: Personal & Contact
    companyName: "",
    email: "",
    phoneNumber: "",
    region: "",
    location: "",
    customLocation: "",
    nicNumber: "",
    contactPerson: "",
    companyLogo: "",
    companyLogoPreview: "",
    // Step 3: Security Setup
    password: "",
    confirmPassword: "",
    acceptedTerms: false,
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch(); 

  const navigate = useNavigate();

  const steps = [
    {
      id: 1,
      title: "Company Information",
      subtitle: "Company Information",
      icon: Building,
      completed: currentStep > 1,
    },
    {
      id: 2,
      title: "Security Setup",
      subtitle: "Secure your account",
      icon: Shield,
      completed: currentStep > 3,
    },
  ];

  useEffect(() => {
    const updateRailMetrics = () => {
      if (!progressRef.current || nodeRefs.current.length === 0) return;
      const containerRect = progressRef.current.getBoundingClientRect();
      const firstNode = nodeRefs.current[0];
      const lastNode = nodeRefs.current[nodeRefs.current.length - 1];
      if (!firstNode || !lastNode) return;
      const firstRect = firstNode.getBoundingClientRect();
      const lastRect = lastNode.getBoundingClientRect();
      const firstCenter = firstRect.left + firstRect.width / 2;
      const lastCenter = lastRect.left + lastRect.width / 2;
      const left = Math.max(0, firstCenter - containerRect.left);
      const width = Math.max(0, lastCenter - firstCenter);
      setRailMetrics({ left, width });
    };

    updateRailMetrics();
    window.addEventListener("resize", updateRailMetrics);
    return () => window.removeEventListener("resize", updateRailMetrics);
  }, [steps.length]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const getFieldError = (field) => {
    return validationErrors[field]?.[0];
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateCompanyName = (name) => {
    return name.length >= 2 && /^[a-zA-Z0-9\s&.-]+$/.test(name);
  };

  const handleNextStep = async () => {
    if (currentStep < 2) {
      const errors = {};
      
      // Email validation
      if (!formData.email) {
        errors.email = ["Email is required"];
      } else if (!validateEmail(formData.email)) {
        errors.email = ["Please enter a valid email address"];
      }
      
      // Company name validation
      if (!formData.companyName) {
        errors.companyName = ["Company name is required"];
      } else if (!validateCompanyName(formData.companyName)) {
        errors.companyName = ["Company name must be at least 2 characters and contain only letters, numbers, spaces, &, ., and -"];
      }
      
      // Phone validation
      if (!formData.phoneNumber) {
        errors.phoneNumber = ["Phone number is required"];
      } else if (!formData.phoneNumber.startsWith("233") || formData.phoneNumber.length !== 12) {
        errors.phoneNumber = ["Please enter a valid Ghana phone number"];
      }
      
      // Location validation
      const locationValid = formData.location || (formData.location === "Other" && formData.customLocation);
      if (!locationValid) {
        errors.location = ["Location is required"];
      }
      
      // Company logo validation
      if (!formData.companyLogo) {
        errors.companyLogo = ["Company logo is required"];
      }
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }
      
      setCurrentStep(currentStep + 1);
      setValidationErrors({});
    } else {
      setIsSubmitting(true);
      
      // Prepare payload with correct field names
      const payload = {
        company_name: formData.companyName,
        region: formData.region,
        location: formData.location === "Other" ? formData.customLocation : formData.location,
        nic_number: formData.nicNumber,
        contact_person: formData.contactPerson,
        email: formData.email,
        phone_number: formData.phoneNumber,
        company_logo: formData.companyLogo,
        password: formData.password,
        password_confirmation: formData.confirmPassword
      };

      try {
        const response = await providerAuthService.register(payload);
        const data = await response?.data?.data;
        
        if (data && data.status_code === 200) {
          toast.success(response.message || "Account created successfully!");
          dispatch(setNewUser(data.data));
          
          navigate("/verify-account", {
            state: {
              message: "Account created successfully! Please verify your account.",
              provider_slug: data?.data?.slug,
              email: formData.email,
              phone: formData.phoneNumber
            },
          });
        } else {
          if (response.validationErrors) {
            setValidationErrors(response.validationErrors);
            toast.error("Please fix the validation errors below.");
          } else {
            toast.error(response.message || "Registration failed!");
          }
        }
      } catch (error) {
        toast.error("An unexpected error occurred during registration.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setValidationErrors({});
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        const phoneValid =
          formData.phoneNumber &&
          formData.phoneNumber.startsWith("233") &&
          formData.phoneNumber.length === 12;

        const locationValid =
          formData.location ||
          (formData.location === "Other" && formData.customLocation);

        const emailValid = formData.email && validateEmail(formData.email);
        const companyNameValid = formData.companyName && validateCompanyName(formData.companyName);

        return (
          companyNameValid &&
          emailValid &&
          phoneValid &&
          formData.region &&
          locationValid &&
          formData.nicNumber &&
          formData.contactPerson &&
          formData.companyLogo
        );
      case 2:
        return (
          formData.password &&
          formData.confirmPassword &&
          formData.password === formData.confirmPassword &&
          formData.password.length >= 6 &&
          formData.acceptedTerms
        );
      default:
        return false;
    }
  };

  return (
    <div className="grid relative grid-cols-1 min-h-screen lg:grid-cols-2 lg:h-screen lg:overflow-hidden">
      {/* Loading Overlay */}
      {isSubmitting && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex fixed inset-0 z-50 justify-center items-center backdrop-blur-sm bg-black/20"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex flex-col gap-4 items-center p-8 bg-white rounded-2xl shadow-2xl"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 rounded-full border-4 border-emerald-500 border-t-transparent"
            />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">
                Creating Your Account
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Please wait while we process your registration...
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
      {/* Left Hero Panel */}
      <motion.div
        className="hidden relative w-full h-full lg:block"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <img
          src={`https://images.unsplash.com/photo-1560264418-c4445382edbc?q=80&w=1470&auto=format&fit=crop`}
          alt="trip-secure-signin"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30" />
        <div className="flex absolute inset-0 flex-col justify-between p-12">
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.8,
              type: "spring",
              bounce: 0.4,
            }}
            className="text-white"
          >
            <motion.h3
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-3 text-2xl font-light"
            >
              Welcome to
            </motion.h3>
            <motion.h2
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-4xl font-bold bg-gradient-to-r from-[#FF6C6C] to-white bg-clip-text text-transparent"
            >
              <Link to="/">
              <img
                src={Images.logo_navbar}
                alt="trip-secure"
                className="h-10"
              />
              </Link>
            </motion.h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="space-y-6"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="p-6 bg-gradient-to-r rounded-xl border backdrop-blur-md from-black/40 to-black/20 border-white/10"
            >
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="mb-4 text-lg italic text-white/90"
              >
                "Travel far, explore freely, and leave the worries behind—our
                trip insurance has you covered every step of the way."
              </motion.p>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.4 }}
                className="flex gap-4 items-center"
              >
                <div className="flex -space-x-3">
                  {[1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 1.4 + i * 0.2 }}
                      className="w-10 h-10 rounded-full border-2 backdrop-blur-sm border-[#05E881]/20 bg-black/30"
                    />
                  ))}
                </div>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2 }}
                  className="text-sm text-white/80"
                >
                  Join our{" "}
                  <span className="text-[#05E881] font-bold">100+</span> members
                </motion.p>
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.2,
                  },
                },
              }}
              className="grid grid-cols-3 gap-4"
            >
              {[
                { number: "100,000k+", label: "Policies Issued" },
                { number: "300+", label: "Registered Insurance Providers" },
                { number: "GHS 1 million+", label: "in claims paid" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                  }}
                  className="p-4 rounded-lg border backdrop-blur-sm transition-all duration-300 bg-black/30 border-white/10"
                >
                  <motion.h4
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2 + index * 0.2 }}
                    className="mb-1 text-xl font-bold text-[#05E881]"
                  >
                    {stat.number}
                  </motion.h4>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.2 + index * 0.2 }}
                    className="text-sm text-white/70"
                  >
                    {stat.label}
                  </motion.p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Right Form Panel */}
      <div className="flex justify-center items-start p-6 h-auto lg:p-12 lg:h-screen lg:overflow-y-auto">
        <div className="w-full max-w-xl">
          {/* Logo for mobile */}
          <div className="flex justify-center mb-6 lg:hidden">
            <Link to="/">
            <img src={Images.logo_colored} alt="TripSecure" className="h-16" />
            </Link>
          </div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-2xl font-bold text-center">Create Account</h1>
            <p className="mt-1 text-sm text-center text-gray-600">
              Join us to Secure Your Journey—Get Covered in Minutes
            </p>
          </motion.div>

          {/* Step Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="relative mt-8 mb-8"
            ref={progressRef}
          >
            {/* Rails constrained between node centers */}
            <div
              className="absolute top-5"
              style={{ left: railMetrics.left, width: railMetrics.width }}
            >
              <div className="h-0.5 bg-gray-200" />
              <div
                className="h-0.5 bg-emerald-500 transition-all duration-500 absolute left-0 top-0"
                style={{
                  width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                }}
              />
            </div>
            <div className="flex relative justify-between items-center">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className="flex flex-col flex-1 items-center"
                >
                  <div
                    className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                      currentStep === step.id
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : step.completed
                        ? "bg-emerald-100 border-emerald-500 text-emerald-600"
                        : "bg-gray-100 border-gray-300 text-gray-400"
                    }`}
                    ref={(el) => (nodeRefs.current[index] = el)}
                  >
                    <step.icon size={16} />
                  </div>
                  <div className="mt-2 text-center">
                    <div
                      className={`text-xs font-medium ${
                        currentStep === step.id || step.completed
                          ? "text-emerald-600"
                          : "text-gray-400"
                      }`}
                    >
                      {step.title}
                    </div>
                    {/* <div className="text-xs text-gray-500">{step.subtitle}</div> */}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Form Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {currentStep === 1 && (
                <PersonalContactStep
                  formData={formData}
                  onChange={handleInputChange}
                  getFieldError={getFieldError}
                  validationErrors={validationErrors}
                />
              )}

              {currentStep === 2 && (
                <SecuritySetupStep
                  formData={formData}
                  onChange={handleInputChange}
                  getFieldError={getFieldError}
                  validationErrors={validationErrors}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-6 space-y-4"
          >
            <motion.button
              onClick={handleNextStep}
              disabled={!isStepValid() || isSubmitting}
              whileHover={{ scale: isStepValid() && !isSubmitting ? 1.02 : 1 }}
              whileTap={{ scale: isStepValid() && !isSubmitting ? 0.98 : 1 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg transition-all duration-300 group hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex relative z-10 gap-2 justify-center items-center">
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-4 h-4 rounded-full border-2 border-white border-t-transparent"
                    />
                    Creating Account...
                  </>
                ) : (
                  <>{currentStep === 2 ? "Create Account" : "Continue"}</>
                )}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full"></div>
            </motion.button>

            {currentStep > 1 && (
              <motion.button
                onClick={handlePrevStep}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-4 py-3 w-full text-sm font-medium text-gray-600 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
              >
                Back
              </motion.button>
            )}

            <p className="text-xs text-center text-gray-600">
              Already have an account?{" "}
              <Link
                to="/signin"
                className="text-emerald-600 transition-colors hover:text-emerald-700"
              >
                Login
              </Link>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// Step 1: Personal & Contact Information
function PersonalContactStep({ formData, onChange, getFieldError, validationErrors }) {
  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        <h3 className="text-lg font-semibold">Company Information</h3>
        <p className="text-xs text-gray-500">
          Let's start with your company information
        </p>
      </motion.div>

      <div className="space-y-3">
        <InputField
          type="text"
          placeholder="Company Name"
          value={formData.companyName}
          onChange={(e) => onChange("companyName", e.target.value)}
          delay={0.2}
          error={getFieldError("companyName")}
          validationError={validationErrors.companyName}
        />

        <LogoUploadField
          value={formData.companyLogo}
          preview={formData.companyLogoPreview}
          onChange={(base64, preview) => {
            onChange("companyLogo", base64);
            onChange("companyLogoPreview", preview);
          }}
          delay={0.23}
          error={getFieldError("companyLogo")}
          validationError={validationErrors.companyLogo}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <InputField
            type="text"
            placeholder="Contact Person"
            value={formData.contactPerson}
            onChange={(e) => onChange("contactPerson", e.target.value)}
            delay={0.25}
          />
          <InputField
            type="text"
            placeholder="NIC Number"
            value={formData.nicNumber}
            onChange={(e) => onChange("nicNumber", e.target.value)}
            delay={0.45}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <InputField
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => onChange("email", e.target.value)}
            delay={0.3}
            error={getFieldError("email")}
            validationError={validationErrors.email}
          />
          <PhoneNumberField
            value={formData.phoneNumber}
            onChange={(value) => onChange("phoneNumber", value)}
            delay={0.35}
            error={getFieldError("phoneNumber")}
            validationError={validationErrors.phoneNumber}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SelectField
            placeholder="Region"
            value={formData.region}
            onChange={(value) => {
              onChange("region", value);
              onChange("location", "");
              onChange("customLocation", "");
            }}
            options={regions}
            delay={0.5}
            searchable={true}
            error={getFieldError("region")}
          />
          <SelectField
            placeholder={
              formData.region ? "Select location" : "Region First"
            }
            value={formData.location}
            onChange={(value) => onChange("location", value)}
            options={formData.region ? locations[formData.region] || [] : []}
            delay={0.55}
            searchable={true}
            disabled={!formData.region}
            error={getFieldError("location")}
          />
        </div>

        {formData.location === "Other" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.4 }}
          >
            <InputField
              type="text"
              placeholder="Enter your location"
              value={formData.customLocation}
              onChange={(e) => onChange("customLocation", e.target.value)}
              delay={0.65}
              error={getFieldError("location")}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
}

// Step 2: Security Setup
function SecuritySetupStep({ formData, onChange, getFieldError, validationErrors }) {
  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        <h3 className="text-lg font-semibold">Security Setup</h3>
        <p className="text-xs text-gray-500">
          Create a secure password for your account
        </p>
      </motion.div>

      <div className="space-y-3">
        <InputField
          type="password"
          placeholder="Password"
          value={formData.password}
          onChange={(e) => onChange("password", e.target.value)}
          delay={0.2}
          error={getFieldError("password")}
          validationError={validationErrors.password}
        />

        <InputField
          type="password"
          placeholder="Confirm Password"
          value={formData.confirmPassword}
          onChange={(e) => onChange("confirmPassword", e.target.value)}
          delay={0.25}
          error={getFieldError("confirmPassword")}
          validationError={validationErrors.confirmPassword}
        />

        {formData.confirmPassword && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`flex items-center gap-2 text-xs ${
              formData.password === formData.confirmPassword
                ? "text-emerald-600"
                : "text-red-500"
            }`}
          >
            <div
              className={`w-2 h-2 rounded-full ${
                formData.password === formData.confirmPassword
                  ? "bg-emerald-500"
                  : "bg-red-500"
              }`}
            />
            {formData.password === formData.confirmPassword
              ? "Passwords match"
              : "Passwords don't match"}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="space-y-1 text-xs text-gray-500"
        >
          <p>Password requirements:</p>
          <ul className="ml-2 space-y-1">
            <li
              className={`transition-colors duration-300 ${
                formData.password.length >= 6
                  ? "text-emerald-600"
                  : "text-gray-400"
              }`}
            >
              • At least 6 characters
            </li>
          </ul>
        </motion.div>

        {/* Terms and Conditions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="flex items-start gap-3 pt-2"
        >
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={formData.acceptedTerms || false}
              onChange={(e) => onChange("acceptedTerms", e.target.checked)}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-2 accent-emerald-500 cursor-pointer"
            />
            <span className="text-sm text-gray-600">
              I have read and accepted the{" "}
              <Link
                to="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 font-medium underline underline-offset-2 hover:text-emerald-700"
              >
                Terms and Conditions
              </Link>
            </span>
          </label>
        </motion.div>
      </div>
    </div>
  );
}

// Reusable Input Field Component
function InputField({ type, placeholder, value, onChange, delay = 0, error, validationError }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
    >
      <div className="rounded-xl border border-gray-200 transition-all duration-300 bg-white/95 group-hover:border-gray-300 group-focus-within:border-emerald-600 group-focus-within:ring-2 group-focus-within:ring-emerald-100">
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full px-4 py-3 text-sm font-medium bg-transparent rounded-[0.95rem] outline-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900 focus:outline-none focus:ring-0 invalid:shadow-none invalid:outline-none invalid:ring-0 transition-all duration-300"
        />
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {validationError && <p className="mt-1 text-xs text-red-500">{validationError}</p>}
    </motion.div>
  );
}

// Reusable Select Field Component with Search
function SelectField({
  placeholder,
  value,
  onChange,
  options,
  delay = 0,
  searchable = false,
  disabled = false,
  error = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredOptions = searchable
    ? options.filter((option) =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const handleOptionClick = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleButtonClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
    >
      <div
        className={`rounded-xl border transition-all duration-300 bg-white/95 ${
          disabled
            ? "bg-gray-100 border-gray-200"
            : error
            ? "border-red-500 group-hover:border-red-400 group-focus-within:border-red-600 group-focus-within:ring-2 group-focus-within:ring-red-100"
            : "border-gray-200 group-hover:border-gray-300 group-focus-within:border-emerald-600 group-focus-within:ring-2 group-focus-within:ring-emerald-100"
        }`}
      >
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={handleButtonClick}
            disabled={disabled}
            className={`flex-1 px-4 py-3 text-sm font-medium bg-transparent rounded-[0.95rem] outline-none transition-all duration-300 text-left ${
              disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer"
            }`}
          >
            <span
              className={value ? "text-gray-900" : "font-normal text-gray-400"}
            >
              {value || placeholder}
            </span>
          </button>

          <div className="flex gap-2 items-center pr-4">
            <ChevronDown
              size={16}
              className={`transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              } ${disabled ? "text-gray-400" : ""}`}
            />
          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: -8, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden absolute bottom-full z-50 py-1 mb-2 w-full max-h-60 bg-white rounded-xl border border-gray-200 shadow-lg"
            >
              {/* Search Input */}
              {searchable && (
                <div className="px-3 py-2 border-b border-gray-100">
                  <div className="relative">
                    <Search
                      size={16}
                      className="absolute left-2 top-1/2 text-gray-400 transform -translate-y-1/2"
                    />
                    <input
                      type="text"
                      placeholder="Search options..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="py-2 pr-3 pl-8 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-300"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}

              {/* Options List */}
              <div className="overflow-y-auto max-h-48">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleOptionClick(option)}
                      className="px-4 py-2 w-full text-sm text-left transition-colors hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none"
                    >
                      {option}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-center text-gray-500">
                    No options found
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-xs text-red-600"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
}

function PhoneNumberField({ value, onChange, delay = 0, error, validationError }) {
  const [displayValue, setDisplayValue] = useState(value || "");

  const formatPhoneNumber = (input) => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // If starts with 0, allow 10 digits (0 + 9 more)
    if (digits.startsWith("0")) {
      return digits.slice(0, 10);
    }
    // Otherwise, allow 9 digits (without leading 0)
    return digits.slice(0, 9);
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input);
    setDisplayValue(formatted);

    // Convert to international format for payload
    let internationalNumber = "";
    if (formatted.startsWith("0")) {
      // If starts with 0, remove it and add 233
      internationalNumber = "233" + formatted.slice(1);
    } else if (formatted.length === 9) {
      // If 9 digits without leading 0, add 233
      internationalNumber = "233" + formatted;
    }

    onChange(internationalNumber);
  };

  const getDisplayText = () => {
    if (!displayValue) return "";
    if (displayValue.startsWith("0")) {
      return displayValue;
    }
    return displayValue;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
    >
      <div
        className={`rounded-xl border transition-all duration-300 bg-white/95 ${
          error
            ? "border-red-500 group-hover:border-red-400 group-focus-within:border-red-600 group-focus-within:ring-2 group-focus-within:ring-red-100"
            : "border-gray-200 group-hover:border-gray-300 group-focus-within:border-emerald-600 group-focus-within:ring-2 group-focus-within:ring-emerald-100"
        }`}
      >
        <div className="flex items-center">
          <div className="px-3 py-3 text-sm font-medium text-gray-600 border-r border-gray-200 bg-gray-50 rounded-l-[0.95rem]">
            233
          </div>
          <input
            type="tel"
            value={getDisplayText()}
            onChange={handleInputChange}
            placeholder="Enter phone number"
            className="flex-1 px-3 py-3 text-sm font-medium bg-transparent rounded-r-[0.95rem] outline-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900 focus:outline-none focus:ring-0 invalid:shadow-none invalid:outline-none invalid:ring-0 transition-all duration-300"
            maxLength={displayValue.startsWith("0") ? 10 : 9}
          />
        </div>
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {displayValue.startsWith("0")
          ? "Enter 10 digits (including the leading 0), e.g., 0501234567"
          : "Enter 9 digits (without leading 0), e.g., 501234567"}
        <br />
        <span className="text-gray-400">Format: 233XXXXXXXXX</span>
      </div>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-xs text-red-600"
        >
          {error}
        </motion.p>
      )}
      {validationError && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-xs text-red-600"
        >
          {validationError}
        </motion.p>
      )}
    </motion.div>
  );
}

// Logo Upload Field Component with Drag & Drop
function LogoUploadField({ value, preview, onChange, delay = 0, error, validationError }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const validateFile = (file) => {
    // Check file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setUploadError("Please upload a valid image file (JPG, PNG, GIF, or WebP)");
      return false;
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setUploadError("File size must be less than 5MB");
      return false;
    }

    setUploadError("");
    return true;
  };

  const handleFile = async (file) => {
    if (!validateFile(file)) return;

    try {
      const base64 = await convertToBase64(file);
      onChange(base64, URL.createObjectURL(file));
    } catch (error) {
      setUploadError("Failed to process image. Please try again.");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleRemove = () => {
    onChange("", "");
    setUploadError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        className="hidden"
      />

      <AnimatePresence mode="wait">
        {!preview ? (
          <motion.div
            key="upload"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
            className={`relative overflow-hidden rounded-xl border-2 border-dashed transition-all duration-300 cursor-pointer ${
              isDragging
                ? "border-emerald-500 bg-emerald-50 scale-[1.02]"
                : error || validationError || uploadError
                ? "border-red-300 bg-red-50 hover:border-red-400"
                : "border-gray-300 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50"
            }`}
          >
            <motion.div
              className="flex flex-col justify-center items-center px-4 py-8"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <motion.div
                animate={
                  isDragging
                    ? { scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }
                    : {}
                }
                transition={{ duration: 0.5 }}
                className={`relative mb-4 p-4 rounded-full ${
                  isDragging
                    ? "bg-emerald-100"
                    : error || validationError || uploadError
                    ? "bg-red-100"
                    : "bg-gray-100 group-hover:bg-emerald-100"
                }`}
              >
                <motion.div
                  animate={isDragging ? { y: [-2, 2, -2] } : {}}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <Upload
                    size={32}
                    className={`${
                      isDragging
                        ? "text-emerald-600"
                        : error || validationError || uploadError
                        ? "text-red-500"
                        : "text-gray-400 group-hover:text-emerald-600"
                    } transition-colors duration-300`}
                  />
                </motion.div>

                {/* Animated particles when dragging */}
                {isDragging && (
                  <>
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-emerald-400 rounded-full"
                        initial={{
                          x: 0,
                          y: 0,
                          opacity: 1,
                        }}
                        animate={{
                          x: Math.cos((i * Math.PI) / 3) * 40,
                          y: Math.sin((i * Math.PI) / 3) * 40,
                          opacity: 0,
                        }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          delay: i * 0.1,
                        }}
                        style={{
                          left: "50%",
                          top: "50%",
                        }}
                      />
                    ))}
                  </>
                )}
              </motion.div>

              <motion.h4
                className={`text-sm font-semibold mb-1 ${
                  isDragging
                    ? "text-emerald-600"
                    : error || validationError || uploadError
                    ? "text-red-600"
                    : "text-gray-700"
                }`}
              >
                {isDragging
                  ? "Drop your logo here"
                  : "Upload Company Logo"}
              </motion.h4>

              <p className="mb-2 text-xs text-center text-gray-500">
                Drag and drop or click to browse
              </p>

              <div className="flex gap-2 items-center text-xs text-gray-400">
                <ImageIcon size={14} />
                <span>JPG, PNG, GIF or WebP (max 5MB)</span>
              </div>

              {/* Animated border effect */}
              <motion.div
                className="absolute inset-0 rounded-xl"
                animate={
                  isDragging
                    ? {
                        boxShadow: [
                          "0 0 0 0 rgba(16, 185, 129, 0.4)",
                          "0 0 0 10px rgba(16, 185, 129, 0)",
                        ],
                      }
                    : {}
                }
                transition={{ duration: 1, repeat: Infinity }}
              />
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="overflow-hidden relative bg-gradient-to-br from-emerald-50 to-white rounded-xl border-2 border-emerald-200"
          >
            <div className="flex gap-4 items-center p-4">
              {/* Logo Preview */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="overflow-hidden relative flex-shrink-0 w-24 h-24 bg-white rounded-lg ring-2 ring-emerald-100 shadow-lg"
              >
                <img
                  src={preview}
                  alt="Company Logo"
                  className="object-contain p-2 w-full h-full"
                />
                
                {/* Success checkmark animation */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="flex absolute top-1 right-1 justify-center items-center w-6 h-6 bg-emerald-500 rounded-full shadow-lg"
                >
                  <motion.svg
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <motion.path
                      d="M2 7L5.5 10.5L12 4"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </motion.div>
              </motion.div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex gap-2 items-center mb-1"
                >
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  <h4 className="text-sm font-semibold text-gray-900">
                    Logo Uploaded Successfully
                  </h4>
                </motion.div>
                
                <motion.p
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mb-2 text-xs text-gray-500"
                >
                  Your company logo is ready
                </motion.p>

                <motion.button
                  type="button"
                  onClick={handleClick}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex gap-1 items-center text-xs font-medium text-emerald-600 transition-colors hover:text-emerald-700"
                >
                  <Upload size={12} />
                  Change Logo
                </motion.button>
              </div>

              {/* Remove Button */}
              <motion.button
                type="button"
                onClick={handleRemove}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, type: "spring" }}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="flex-shrink-0 p-2 text-red-500 bg-red-50 rounded-lg transition-colors hover:bg-red-100"
              >
                <X size={18} />
              </motion.button>
            </div>

            {/* Animated gradient border */}
            <motion.div
              className="absolute inset-0 rounded-xl pointer-events-none"
              animate={{
                background: [
                  "linear-gradient(0deg, rgba(16, 185, 129, 0.1) 0%, transparent 100%)",
                  "linear-gradient(360deg, rgba(16, 185, 129, 0.1) 0%, transparent 100%)",
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Messages */}
      <AnimatePresence>
        {(error || validationError || uploadError) && (
          <motion.div
            initial={{ opacity: 0, y: -5, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -5, height: 0 }}
            className="overflow-hidden"
          >
            <motion.p
              initial={{ x: -10 }}
              animate={{ x: 0 }}
              className="flex gap-1 items-center mt-2 text-xs text-red-600"
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.3 }}
              >
                ⚠️
              </motion.span>
              {error || validationError || uploadError}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
